import { useEffect } from "react";

interface Props {
  open: boolean;
  title: string;
  message?: string;
  /** Texto del CTA principal. Default: "Sí, continuar" */
  confirmLabel?: string;
  /** Texto del botón secundario. Default: "Cancelar" */
  cancelLabel?: string;
  /** Variante visual del CTA. "danger" lo pinta rojo. */
  variant?: "default" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal de confirmación que reemplaza al `window.confirm` nativo.
 *
 * Se cierra con Escape (si !busy) y con click en el backdrop. Enter
 * dispara la acción de confirmar.
 */
export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Sí, continuar",
  cancelLabel = "Cancelar",
  variant = "default",
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
      if (e.key === "Enter" && !busy) onConfirm();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="modal-panel" style={{ maxWidth: 460 }}>
        <div className="modal-inner" style={{ padding: 24 }}>
          <div
            className="eyebrow"
            style={{
              marginBottom: 6,
              color: variant === "danger" ? "var(--err)" : "var(--ink-3)",
            }}
          >
            Confirmación
          </div>
          <h2
            id="confirm-title"
            style={{ margin: 0, fontSize: 20, lineHeight: 1.3 }}
          >
            {title}
          </h2>
          {message && (
            <p
              style={{
                marginTop: 10,
                marginBottom: 0,
                color: "var(--ink-2)",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {message}
            </p>
          )}
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 20,
            }}
          >
            <button
              type="button"
              className="btn-outline"
              onClick={onCancel}
              disabled={busy}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className="cta"
              style={
                variant === "danger"
                  ? {
                      width: "auto",
                      background: "var(--err)",
                      borderColor: "var(--err)",
                    }
                  : { width: "auto" }
              }
              onClick={onConfirm}
              disabled={busy}
              autoFocus
            >
              <span className="cta-inner">
                {busy ? "Procesando…" : confirmLabel}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
